import { z } from "zod";
import { PROTOCOL_CONSTANTS, W10_DATA_CHANNEL_KINDS, W10_DATA_CHANNEL_V1, } from "./constants.js";
const openObject = (shape) => z.object(shape).passthrough();
/** Returns the number of bytes used by the string's standard UTF-8 encoding. */
export function utf8ByteLength(value) {
    let bytes = 0;
    for (const character of value) {
        const codePoint = character.codePointAt(0);
        if (codePoint === undefined) {
            continue;
        }
        if (codePoint <= 0x7f) {
            bytes += 1;
        }
        else if (codePoint <= 0x7ff) {
            bytes += 2;
        }
        else if (codePoint <= 0xffff) {
            bytes += 3;
        }
        else {
            bytes += 4;
        }
    }
    return bytes;
}
/** Truncates at a Unicode code-point boundary without exceeding a UTF-8 byte limit. */
export function truncateUtf8(value, maximumBytes) {
    let bytes = 0;
    let result = "";
    for (const character of value) {
        const characterBytes = utf8ByteLength(character);
        if (bytes + characterBytes > maximumBytes) {
            break;
        }
        result += character;
        bytes += characterBytes;
    }
    return result;
}
const unsafeDisplayNameCharacters = new RegExp(String.raw `[\u0000-\u001f\u007f-\u009f\u200b-\u200d\u202a-\u202e\u2066-\u2069\ufeff]`, "gu");
/** Applies the host-side display-name hygiene required by SPEC section 3. */
export function normalizeDisplayName(value) {
    return value
        .normalize("NFC")
        .replace(unsafeDisplayNameCharacters, "")
        .replace(/\s+/gu, " ")
        .trim()
        .normalize("NFC");
}
const normalizedDisplayNameSchema = z.string().superRefine((value, context) => {
    const codePoints = [...value].length;
    if (codePoints < 1 || codePoints > 64) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "displayName must contain 1 to 64 Unicode code points after normalization",
        });
    }
    if (utf8ByteLength(value) > 256) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "displayName must not exceed 256 UTF-8 bytes after normalization",
        });
    }
    if (/^host$/iu.test(value)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "displayName must not render as the reserved host name",
        });
    }
});
export const displayNameSchema = z
    .string()
    .transform(normalizeDisplayName)
    .pipe(normalizedDisplayNameSchema);
export const userAgentSchema = z
    .string()
    .transform((value) => truncateUtf8(value, 512));
export const clientIdSchema = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu, "clientId must be a UUID v4");
export const uuidSchema = z.string().uuid();
// These are conservative encoded-length floors, not entropy estimators. The
// generators still MUST provide >=64 bits (§3) and >=128 bits (§3), respectively.
export const connIdSchema = z.string().min(8);
export const resumeTokenSchema = z.string().min(16);
export const grantIdSchema = z.string().min(1);
export const legIdSchema = z.string().min(1);
export const peerIdSchema = z.union([z.literal("host"), clientIdSchema]);
export const deviceClassSchema = z.enum([
    "desktop",
    "android",
    "chromebook",
    "webos",
    "ios",
    "unknown",
]);
export const capabilitiesSchema = openObject({
    canCaptureScreen: z.boolean(),
    deviceClass: deviceClassSchema,
    h264Only: z.boolean(),
    userAgent: userAgentSchema,
}).transform((capabilities) => capabilities.deviceClass === "webos"
    ? { ...capabilities, h264Only: true }
    : capabilities);
export const idleShareStateSchema = openObject({
    status: z.literal("idle"),
    shareId: z.never().optional(),
    sourceClientId: z.never().optional(),
    grantId: z.never().optional(),
    startedAt: z.never().optional(),
});
export const pendingShareStateSchema = openObject({
    status: z.literal("pending"),
    shareId: uuidSchema,
    sourceClientId: peerIdSchema,
    grantId: grantIdSchema.optional(),
    startedAt: z.never().optional(),
});
export const activeShareStateSchema = openObject({
    status: z.literal("active"),
    shareId: uuidSchema,
    sourceClientId: peerIdSchema,
    grantId: z.never().optional(),
    startedAt: z.number().finite().nonnegative(),
});
export const shareStateSchema = z.discriminatedUnion("status", [
    idleShareStateSchema,
    pendingShareStateSchema,
    activeShareStateSchema,
]);
export const peerConnectionSchema = z.enum(["active", "zombie"]);
export const peerInfoSchema = openObject({
    clientId: peerIdSchema,
    displayName: displayNameSchema,
    capabilities: capabilitiesSchema,
    connection: peerConnectionSchema,
    /** Host-authority marker; displayName is never a trust signal. */
    verifiedBoard: z.boolean().optional(),
}).superRefine((peer, context) => {
    if (peer.clientId === "host" && peer.verifiedBoard !== true) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["verifiedBoard"],
            message: "the authoritative host peer must be a verified board",
        });
    }
    if (peer.clientId !== "host" && peer.verifiedBoard === true) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["verifiedBoard"],
            message: "a browser client cannot claim verified-board authority",
        });
    }
});
export const hostMediaStateSchema = z.enum(["recovering", "ok"]);
export const roomStateSchema = openObject({
    roomId: z.string().min(1),
    sessionId: uuidSchema,
    peers: z.array(peerInfoSchema),
    share: shareStateSchema,
    hostMedia: hostMediaStateSchema,
});
export const protoRangeSchema = openObject({
    min: z.number().int().positive(),
    max: z.number().int().positive(),
}).superRefine((value, context) => {
    if (value.min > value.max) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "proto.min must be less than or equal to proto.max",
            path: ["min"],
        });
    }
});
const joinPayloadCommonShape = {
    clientId: clientIdSchema,
    connId: connIdSchema,
    displayName: displayNameSchema,
    capabilities: capabilitiesSchema,
    proto: protoRangeSchema,
    appVersion: z.string().optional(),
};
export const freshJoinPayloadSchema = openObject({
    ...joinPayloadCommonShape,
    resume: z.literal(false),
    resumeToken: resumeTokenSchema.optional(),
});
export const resumeJoinPayloadSchema = openObject({
    ...joinPayloadCommonShape,
    resume: z.literal(true),
    resumeToken: resumeTokenSchema,
});
export const joinPayloadSchema = z.discriminatedUnion("resume", [
    freshJoinPayloadSchema,
    resumeJoinPayloadSchema,
]);
export const heartbeatConfigSchema = openObject({
    intervalMs: z.number().int().positive(),
    timeoutMs: z.number().int().positive(),
});
export const joinAckSuccessPayloadSchema = openObject({
    ok: z.literal(true),
    resumed: z.boolean(),
    resumeToken: resumeTokenSchema,
    yjsTicket: z.string().min(1),
    proto: z.number().int().positive(),
    hostVersion: z.string(),
    sessionId: uuidSchema,
    roomState: roomStateSchema,
    heartbeat: heartbeatConfigSchema,
});
export const joinFailureCodeSchema = z.enum([
    "ROOM_FULL",
    "VERSION_MISMATCH",
    "INVALID_PAYLOAD",
    "RESUME_UNAUTHORIZED",
    "LICENSE_EXPIRED",
    "LICENSE_INVALID",
    "ROOM_CLOSED",
    "RATE_LIMITED",
]);
const versionMismatchJoinAckFailurePayloadSchema = openObject({
    ok: z.literal(false),
    code: z.literal("VERSION_MISMATCH"),
    message: z.string(),
    data: protoRangeSchema,
});
const otherJoinAckFailurePayloadSchema = openObject({
    ok: z.literal(false),
    code: z.enum([
        "ROOM_FULL",
        "INVALID_PAYLOAD",
        "RESUME_UNAUTHORIZED",
        "LICENSE_EXPIRED",
        "LICENSE_INVALID",
        "ROOM_CLOSED",
        "RATE_LIMITED",
    ]),
    message: z.string(),
    data: z.unknown().optional(),
});
export const joinAckFailurePayloadSchema = z.discriminatedUnion("code", [
    versionMismatchJoinAckFailurePayloadSchema,
    otherJoinAckFailurePayloadSchema,
]);
export const joinAckPayloadSchema = z.union([
    joinAckSuccessPayloadSchema,
    joinAckFailurePayloadSchema,
]);
export const emptyPayloadSchema = openObject({});
export const kickReasonSchema = z.enum(["superseded", "room-closed", "admin"]);
// §14 makes supersededBy optional for every kick reason. A conforming host
// supplies it for a supersede (§8.3), while receivers tolerate its absence.
export const kickPayloadSchema = openObject({
    reason: kickReasonSchema,
    supersededBy: connIdSchema.optional(),
});
export const roomStatePayloadSchema = openObject({
    roomState: roomStateSchema,
});
export const peerJoinedPayloadSchema = openObject({ peer: peerInfoSchema });
export const peerLeftPayloadSchema = openObject({
    clientId: peerIdSchema,
    reason: z.enum(["leave", "timeout", "kick"]),
});
export const peerUpdatedPayloadSchema = openObject({ peer: peerInfoSchema });
export const peerUpdatePayloadSchema = openObject({
    displayName: displayNameSchema.optional(),
    capabilities: capabilitiesSchema.optional(),
});
export const shareDecisionReasonSchema = z.enum([
    "denied",
    "busy",
    "unsupported-device",
    "already-sharing",
]);
export const grantedShareDecisionPayloadSchema = openObject({
    granted: z.literal(true),
    grantId: grantIdSchema,
    reason: z.never().optional(),
});
export const deniedShareDecisionPayloadSchema = openObject({
    granted: z.literal(false),
    grantId: z.never().optional(),
    reason: shareDecisionReasonSchema.optional(),
});
export const shareDecisionPayloadSchema = z.discriminatedUnion("granted", [
    grantedShareDecisionPayloadSchema,
    deniedShareDecisionPayloadSchema,
]);
export const shareReadyPayloadSchema = openObject({ grantId: grantIdSchema });
export const shareStopReasonSchema = z.enum(["cancelled", "capture-failed"]);
export const shareStopPayloadSchema = openObject({
    reason: shareStopReasonSchema,
});
export const shareRevokePayloadSchema = openObject({
    reason: z.string().min(1),
});
export const shareChangedPayloadSchema = openObject({
    share: shareStateSchema,
});
export const hostMediaPayloadSchema = openObject({
    state: hostMediaStateSchema,
});
export const legRoleSchema = z.enum(["offerer", "answerer"]);
export const legCreatePayloadSchema = openObject({
    legId: legIdSchema,
    epoch: z.number().int().positive(),
    role: legRoleSchema,
    peer: peerIdSchema,
    h264Only: z.boolean(),
});
export const legClosePayloadSchema = openObject({ legId: legIdSchema });
export const rtcIceConnectionStateSchema = z.enum([
    "new",
    "checking",
    "connected",
    "completed",
    "disconnected",
    "failed",
    "closed",
]);
// The host intentionally accepts unknown state strings and ignores them (SPEC section 6.2).
export const legStatusPayloadSchema = openObject({
    legId: legIdSchema,
    state: z.string(),
});
export const transportModeSchema = z.enum(["direct", "relay", "unreachable"]);
export const transportChangedPayloadSchema = openObject({
    mode: transportModeSchema,
});
export const rtcOfferDataSchema = openObject({
    type: z.literal("offer"),
    sdp: z.string().optional(),
});
export const rtcAnswerDataSchema = openObject({
    type: z.literal("answer"),
    sdp: z.string().optional(),
});
export const rtcIceCandidateDataSchema = openObject({
    candidate: z.string().min(1),
    sdpMid: z.string().nullish(),
    sdpMLineIndex: z.number().int().nonnegative().nullish(),
    usernameFragment: z.string().nullish(),
});
const clientRtcSignalVariants = [
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        kind: z.literal("offer"),
        data: rtcOfferDataSchema,
    }),
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        kind: z.literal("answer"),
        data: rtcAnswerDataSchema,
    }),
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        kind: z.literal("ice"),
        data: rtcIceCandidateDataSchema,
    }),
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        kind: z.literal("ice-restart-offer"),
        data: rtcOfferDataSchema,
    }),
];
export const clientRtcSignalPayloadSchema = z.discriminatedUnion("kind", clientRtcSignalVariants);
const relayedRtcSignalVariants = [
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        from: peerIdSchema,
        kind: z.literal("offer"),
        data: rtcOfferDataSchema,
    }),
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        from: peerIdSchema,
        kind: z.literal("answer"),
        data: rtcAnswerDataSchema,
    }),
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        from: peerIdSchema,
        kind: z.literal("ice"),
        data: rtcIceCandidateDataSchema,
    }),
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        from: peerIdSchema,
        kind: z.literal("ice-restart-offer"),
        data: rtcOfferDataSchema,
    }),
];
export const rtcSignalPayloadSchema = z.discriminatedUnion("kind", relayedRtcSignalVariants);
const anyRtcSignalVariants = [
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        from: peerIdSchema.optional(),
        kind: z.literal("offer"),
        data: rtcOfferDataSchema,
    }),
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        from: peerIdSchema.optional(),
        kind: z.literal("answer"),
        data: rtcAnswerDataSchema,
    }),
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        from: peerIdSchema.optional(),
        kind: z.literal("ice"),
        data: rtcIceCandidateDataSchema,
    }),
    openObject({
        legId: legIdSchema,
        to: peerIdSchema,
        from: peerIdSchema.optional(),
        kind: z.literal("ice-restart-offer"),
        data: rtcOfferDataSchema,
    }),
];
export const anyRtcSignalPayloadSchema = z.discriminatedUnion("kind", anyRtcSignalVariants);
export const statsReportPayloadSchema = openObject({
    legId: legIdSchema,
    candidatePairType: z.string(),
    rttMs: z.number().finite().nonnegative(),
    bytesReceived: z.number().int().nonnegative(),
    framesDecoded: z.number().int().nonnegative(),
    freezeCount: z.number().int().nonnegative(),
});
export const errorPayloadSchema = openObject({
    code: z.string().min(1),
    message: z.string(),
    fatal: z.boolean(),
    refSeq: z.number().int().positive().optional(),
});
export const debugForceTransportPayloadSchema = openObject({
    clientId: clientIdSchema,
    mode: z.literal("relay"),
});
export const protocolVersionSchema = z
    .number()
    .int()
    .min(PROTOCOL_CONSTANTS.PROTO_VERSION_MIN)
    .max(PROTOCOL_CONSTANTS.PROTO_VERSION_MAX);
export const protocolEnvelopeSchema = openObject({
    v: protocolVersionSchema,
    type: z.string().regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/u),
    seq: z.number().int().positive(),
    payload: z.record(z.unknown()).optional(),
});
const W10_MEMBER_IDENTITY_KEYS = [
    "sessionId",
    "memberLegId",
    "memberEpoch",
];
const W10_PRIMARY_JOIN_ACK_SUCCESS_KEYS = [
    "ok",
    "resumed",
    "resumeToken",
    "proto",
    "hostVersion",
    "sessionId",
    "roomState",
    "heartbeat",
    "memberLegId",
    "memberEpoch",
];
const W10_PRIMARY_JOIN_ACK_FAILURE_KEYS = ["ok", "code", "message"];
const W10_RESYNC_READY_KEYS = [
    ...W10_MEMBER_IDENTITY_KEYS,
    "resyncId",
    "boardSyncStep2MessageId",
    "boardSyncStep1MessageId",
    "pageSyncStep1MessageId",
    "pageSyncStep2MessageId",
    "coveredBoardDirtyRevision",
    "coveredPageDirtyGeneration",
    "boardDisposition",
    "currentBoardDirtyRevision",
];
const W10_RESYNC_READY_ACK_KEYS = [
    ...W10_RESYNC_READY_KEYS,
    "pageDisposition",
    "currentPageDirtyGeneration",
];
const W10_RESYNC_COMPLETE_KEYS = [
    ...W10_RESYNC_READY_ACK_KEYS,
    "completionDisposition",
    "completionBoardDirtyRevision",
    "completionPageDirtyGeneration",
];
const W10_AWARENESS_REJECTION_REASONS = new Set([
    "AWARENESS_CLIENT_MISMATCH",
    "AWARENESS_CLIENT_COLLISION",
    "AWARENESS_CLIENT_LIMIT",
    "AWARENESS_BYTE_LIMIT",
    "AWARENESS_CLOCK_EXHAUSTED",
]);
function w10JsonRecord(value, label) {
    if (typeof value !== "object" || value === null || Array.isArray(value))
        throw new TypeError(`${label} must be an object`);
    return value;
}
function w10ExactKeys(value, expected, label) {
    const actual = Object.keys(value);
    if (actual.length !== expected.length ||
        actual.some((key, index) => key !== expected[index])) {
        throw new TypeError(`${label} has invalid fields or field order`);
    }
}
function w10SchemaValue(schema, value, label) {
    const parsed = schema.safeParse(value);
    if (!parsed.success)
        throw new TypeError(`${label} is invalid`, { cause: parsed.error });
    return parsed.data;
}
function w10NonemptyString(value, label) {
    if (typeof value !== "string" || value.length === 0)
        throw new TypeError(`${label} must be a non-empty string`);
    return value;
}
function w10PositiveSafeInteger(value, label) {
    if (!Number.isSafeInteger(value) || value <= 0)
        throw new TypeError(`${label} must be a positive safe integer`);
    return value;
}
function w10NonnegativeSafeInteger(value, label) {
    if (!Number.isSafeInteger(value) || value < 0)
        throw new TypeError(`${label} must be a non-negative safe integer`);
    return value;
}
function w10PositiveUint32(value, label) {
    const parsed = w10PositiveSafeInteger(value, label);
    if (parsed > 0xffff_ffff)
        throw new TypeError(`${label} must be a positive uint32`);
    return parsed;
}
function w10Uint32(value, label) {
    const parsed = w10NonnegativeSafeInteger(value, label);
    if (parsed > 0xffff_ffff)
        throw new TypeError(`${label} must be a uint32`);
    return parsed;
}
function w10Disposition(value, label) {
    if (value !== "ready" && value !== "restart")
        throw new TypeError(`${label} must be ready or restart`);
    return value;
}
function readW10MemberIdentity(value) {
    return {
        sessionId: w10NonemptyString(value.sessionId, "W10 sessionId"),
        memberLegId: w10NonemptyString(value.memberLegId, "W10 memberLegId"),
        memberEpoch: w10PositiveSafeInteger(value.memberEpoch, "W10 memberEpoch"),
    };
}
function readW10ResyncReady(value) {
    return {
        ...readW10MemberIdentity(value),
        resyncId: w10PositiveSafeInteger(value.resyncId, "W10 resyncId"),
        boardSyncStep2MessageId: w10PositiveUint32(value.boardSyncStep2MessageId, "W10 board SyncStep2 messageId"),
        boardSyncStep1MessageId: w10PositiveUint32(value.boardSyncStep1MessageId, "W10 board SyncStep1 messageId"),
        pageSyncStep1MessageId: w10PositiveUint32(value.pageSyncStep1MessageId, "W10 page SyncStep1 messageId"),
        pageSyncStep2MessageId: w10PositiveUint32(value.pageSyncStep2MessageId, "W10 page SyncStep2 messageId"),
        coveredBoardDirtyRevision: w10NonnegativeSafeInteger(value.coveredBoardDirtyRevision, "W10 covered board dirty revision"),
        coveredPageDirtyGeneration: w10NonnegativeSafeInteger(value.coveredPageDirtyGeneration, "W10 covered page dirty generation"),
        boardDisposition: w10Disposition(value.boardDisposition, "W10 board disposition"),
        currentBoardDirtyRevision: w10NonnegativeSafeInteger(value.currentBoardDirtyRevision, "W10 current board dirty revision"),
    };
}
function readW10ExactControlRecord(type, input) {
    if (typeof type !== "string")
        throw new TypeError("W10 control type must be a string");
    const value = w10JsonRecord(input, `${type} payload`);
    switch (type) {
        case "join-ack": {
            if (value.ok === false) {
                w10ExactKeys(value, Object.prototype.hasOwnProperty.call(value, "data")
                    ? [...W10_PRIMARY_JOIN_ACK_FAILURE_KEYS, "data"]
                    : W10_PRIMARY_JOIN_ACK_FAILURE_KEYS, `${type} payload`);
                const failure = joinAckFailurePayloadSchema.safeParse(value);
                if (!failure.success)
                    throw new TypeError("invalid primary W10 failed join-ack payload", {
                        cause: failure.error,
                    });
                return Object.freeze({
                    type,
                    payload: Object.freeze(failure.data),
                });
            }
            w10ExactKeys(value, W10_PRIMARY_JOIN_ACK_SUCCESS_KEYS, `${type} payload`);
            if (value.ok !== true)
                throw new TypeError("primary W10 join-ack ok must be true");
            return Object.freeze({
                type,
                payload: Object.freeze({
                    ok: true,
                    resumed: w10SchemaValue(z.boolean(), value.resumed, "primary W10 join-ack resumed"),
                    resumeToken: w10SchemaValue(resumeTokenSchema, value.resumeToken, "primary W10 join-ack resumeToken"),
                    proto: w10SchemaValue(z.number().int().positive(), value.proto, "primary W10 join-ack proto"),
                    hostVersion: w10SchemaValue(z.string(), value.hostVersion, "primary W10 join-ack hostVersion"),
                    sessionId: w10SchemaValue(uuidSchema, value.sessionId, "primary W10 join-ack sessionId"),
                    roomState: w10SchemaValue(roomStateSchema, value.roomState, "primary W10 join-ack roomState"),
                    heartbeat: w10SchemaValue(heartbeatConfigSchema, value.heartbeat, "primary W10 join-ack heartbeat"),
                    memberLegId: w10NonemptyString(value.memberLegId, "W10 memberLegId"),
                    memberEpoch: w10PositiveSafeInteger(value.memberEpoch, "W10 memberEpoch"),
                }),
            });
        }
        case "member-ready":
        case "member-ready-ack": {
            w10ExactKeys(value, W10_MEMBER_IDENTITY_KEYS, `${type} payload`);
            return Object.freeze({
                type,
                payload: Object.freeze(readW10MemberIdentity(value)),
            });
        }
        case "whiteboard-awareness-rejected": {
            w10ExactKeys(value, [
                ...W10_MEMBER_IDENTITY_KEYS,
                "sourceWhiteboardMessageId",
                "clientId",
                "sourceClock",
                "reason",
            ], `${type} payload`);
            if (typeof value.reason !== "string" ||
                !W10_AWARENESS_REJECTION_REASONS.has(value.reason)) {
                throw new TypeError("invalid W10 awareness rejection reason");
            }
            return Object.freeze({
                type,
                payload: Object.freeze({
                    ...readW10MemberIdentity(value),
                    sourceWhiteboardMessageId: w10PositiveUint32(value.sourceWhiteboardMessageId, "W10 source whiteboard messageId"),
                    clientId: w10Uint32(value.clientId, "W10 awareness clientId"),
                    sourceClock: w10NonnegativeSafeInteger(value.sourceClock, "W10 awareness source clock"),
                    reason: value.reason,
                }),
            });
        }
        case "whiteboard-resync-needed": {
            w10ExactKeys(value, [...W10_MEMBER_IDENTITY_KEYS, "pageDirtyGeneration"], `${type} payload`);
            return Object.freeze({
                type,
                payload: Object.freeze({
                    ...readW10MemberIdentity(value),
                    pageDirtyGeneration: w10NonnegativeSafeInteger(value.pageDirtyGeneration, "W10 page dirty generation"),
                }),
            });
        }
        case "whiteboard-resync-request": {
            w10ExactKeys(value, [
                ...W10_MEMBER_IDENTITY_KEYS,
                "resyncId",
                "boardDirtyRevision",
                "afterWhiteboardMessageId",
            ], `${type} payload`);
            return Object.freeze({
                type,
                payload: Object.freeze({
                    ...readW10MemberIdentity(value),
                    resyncId: w10PositiveSafeInteger(value.resyncId, "W10 resyncId"),
                    boardDirtyRevision: w10NonnegativeSafeInteger(value.boardDirtyRevision, "W10 board dirty revision"),
                    afterWhiteboardMessageId: w10Uint32(value.afterWhiteboardMessageId, "W10 after whiteboard messageId"),
                }),
            });
        }
        case "whiteboard-resync-start":
        case "whiteboard-resync-start-ack": {
            w10ExactKeys(value, [...W10_MEMBER_IDENTITY_KEYS, "resyncId", "pageDirtyGeneration"], `${type} payload`);
            return Object.freeze({
                type,
                payload: Object.freeze({
                    ...readW10MemberIdentity(value),
                    resyncId: w10PositiveSafeInteger(value.resyncId, "W10 resyncId"),
                    pageDirtyGeneration: w10NonnegativeSafeInteger(value.pageDirtyGeneration, "W10 page dirty generation"),
                }),
            });
        }
        case "whiteboard-resync-ready": {
            w10ExactKeys(value, W10_RESYNC_READY_KEYS, `${type} payload`);
            return Object.freeze({
                type,
                payload: Object.freeze(readW10ResyncReady(value)),
            });
        }
        case "whiteboard-resync-ready-ack": {
            w10ExactKeys(value, W10_RESYNC_READY_ACK_KEYS, `${type} payload`);
            return Object.freeze({
                type,
                payload: Object.freeze({
                    ...readW10ResyncReady(value),
                    pageDisposition: w10Disposition(value.pageDisposition, "W10 page disposition"),
                    currentPageDirtyGeneration: w10NonnegativeSafeInteger(value.currentPageDirtyGeneration, "W10 current page dirty generation"),
                }),
            });
        }
        case "whiteboard-resync-complete": {
            w10ExactKeys(value, W10_RESYNC_COMPLETE_KEYS, `${type} payload`);
            return Object.freeze({
                type,
                payload: Object.freeze({
                    ...readW10ResyncReady(value),
                    pageDisposition: w10Disposition(value.pageDisposition, "W10 page disposition"),
                    currentPageDirtyGeneration: w10NonnegativeSafeInteger(value.currentPageDirtyGeneration, "W10 current page dirty generation"),
                    completionDisposition: w10Disposition(value.completionDisposition, "W10 completion disposition"),
                    completionBoardDirtyRevision: w10NonnegativeSafeInteger(value.completionBoardDirtyRevision, "W10 completion board dirty revision"),
                    completionPageDirtyGeneration: w10NonnegativeSafeInteger(value.completionPageDirtyGeneration, "W10 completion page dirty generation"),
                }),
            });
        }
        default:
            throw new TypeError(`unsupported exact W10 control type: ${type}`);
    }
}
const W10_JSON_NUMBER = /-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?(?:[eE][+-]?[0-9]+)?/uy;
function assertW10JsonHasUniqueMembers(source) {
    let index = 0;
    const frames = [{ kind: "root", state: "value" }];
    const invalid = () => {
        throw new TypeError("W10 control record is invalid JSON");
    };
    const skipWhitespace = () => {
        while (source[index] === " " ||
            source[index] === "\t" ||
            source[index] === "\n" ||
            source[index] === "\r") {
            index += 1;
        }
    };
    const readString = () => {
        const start = index;
        index += 1;
        while (index < source.length) {
            const code = source.charCodeAt(index);
            if (code === 0x22) {
                index += 1;
                let parsed;
                try {
                    parsed = JSON.parse(source.slice(start, index));
                }
                catch {
                    return invalid();
                }
                if (typeof parsed !== "string")
                    return invalid();
                for (let offset = 0; offset < parsed.length; offset += 1) {
                    const first = parsed.charCodeAt(offset);
                    if (first >= 0xd800 && first <= 0xdbff) {
                        const second = parsed.charCodeAt(offset + 1);
                        if (!(second >= 0xdc00 && second <= 0xdfff))
                            return invalid();
                        offset += 1;
                    }
                    else if (first >= 0xdc00 && first <= 0xdfff) {
                        return invalid();
                    }
                }
                return parsed;
            }
            if (code < 0x20)
                return invalid();
            if (code === 0x5c) {
                index += 1;
                const escape = source[index];
                if (escape === "u") {
                    if (!/^[0-9A-Fa-f]{4}$/u.test(source.slice(index + 1, index + 5)))
                        return invalid();
                    index += 5;
                }
                else if (escape === '"' ||
                    escape === "\\" ||
                    escape === "/" ||
                    escape === "b" ||
                    escape === "f" ||
                    escape === "n" ||
                    escape === "r" ||
                    escape === "t") {
                    index += 1;
                }
                else {
                    return invalid();
                }
            }
            else {
                if (code >= 0xd800 && code <= 0xdbff) {
                    const low = source.charCodeAt(index + 1);
                    if (!(low >= 0xdc00 && low <= 0xdfff))
                        return invalid();
                    index += 2;
                }
                else {
                    if (code >= 0xdc00 && code <= 0xdfff)
                        return invalid();
                    index += 1;
                }
            }
        }
        return invalid();
    };
    const beginValue = (frame) => {
        if (frame.kind === "root")
            frame.state = "done";
        else
            frame.state = "comma-or-end";
        const character = source[index];
        if (character === "{") {
            index += 1;
            frames.push({ kind: "object", state: "key-or-end", keys: new Set() });
        }
        else if (character === "[") {
            index += 1;
            frames.push({ kind: "array", state: "value-or-end" });
        }
        else if (character === '"') {
            readString();
        }
        else if (source.startsWith("true", index)) {
            index += 4;
        }
        else if (source.startsWith("false", index)) {
            index += 5;
        }
        else if (source.startsWith("null", index)) {
            index += 4;
        }
        else {
            W10_JSON_NUMBER.lastIndex = index;
            const match = W10_JSON_NUMBER.exec(source);
            if (match === null || match.index !== index)
                return invalid();
            index = W10_JSON_NUMBER.lastIndex;
        }
    };
    while (frames.length > 0) {
        skipWhitespace();
        const frame = frames[frames.length - 1];
        if (frame === undefined)
            break;
        const character = source[index];
        if (frame.kind === "root") {
            if (frame.state === "done") {
                if (index !== source.length)
                    invalid();
                frames.pop();
            }
            else {
                beginValue(frame);
            }
        }
        else if (frame.kind === "array") {
            if (frame.state === "value-or-end") {
                if (character === "]") {
                    index += 1;
                    frames.pop();
                }
                else {
                    beginValue(frame);
                }
            }
            else if (frame.state === "value") {
                beginValue(frame);
            }
            else if (character === ",") {
                index += 1;
                frame.state = "value";
            }
            else if (character === "]") {
                index += 1;
                frames.pop();
            }
            else {
                invalid();
            }
        }
        else if (frame.state === "key-or-end" && character === "}") {
            index += 1;
            frames.pop();
        }
        else if (frame.state === "key-or-end" || frame.state === "key") {
            if (character !== '"')
                invalid();
            const key = readString();
            if (frame.keys.has(key))
                throw new TypeError("W10 control record contains duplicate members");
            frame.keys.add(key);
            frame.state = "colon";
        }
        else if (frame.state === "colon") {
            if (character !== ":")
                invalid();
            index += 1;
            frame.state = "value";
        }
        else if (frame.state === "value") {
            beginValue(frame);
        }
        else if (character === ",") {
            index += 1;
            frame.state = "key";
        }
        else if (character === "}") {
            index += 1;
            frames.pop();
        }
        else {
            invalid();
        }
    }
}
function w10Utf8Continuation(value) {
    return value !== undefined && value >= 0x80 && value <= 0xbf;
}
/** Strict UTF-8 decoder independent of Node and DOM library declarations. */
function decodeW10Utf8(bytes) {
    if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)
        throw new TypeError("W10 control record must not contain a BOM");
    let text = "";
    for (let index = 0; index < bytes.length;) {
        const first = bytes[index];
        if (first === undefined)
            break;
        if (first <= 0x7f) {
            text += String.fromCodePoint(first);
            index += 1;
            continue;
        }
        const second = bytes[index + 1];
        if (first >= 0xc2 && first <= 0xdf && w10Utf8Continuation(second)) {
            text += String.fromCodePoint(((first & 0x1f) << 6) | (second & 0x3f));
            index += 2;
            continue;
        }
        const third = bytes[index + 2];
        if (w10Utf8Continuation(second) &&
            w10Utf8Continuation(third) &&
            ((first === 0xe0 && second >= 0xa0) ||
                (first >= 0xe1 && first <= 0xec) ||
                (first === 0xed && second <= 0x9f) ||
                (first >= 0xee && first <= 0xef))) {
            text += String.fromCodePoint(((first & 0x0f) << 12) | ((second & 0x3f) << 6) | (third & 0x3f));
            index += 3;
            continue;
        }
        const fourth = bytes[index + 3];
        if (w10Utf8Continuation(second) &&
            w10Utf8Continuation(third) &&
            w10Utf8Continuation(fourth) &&
            ((first === 0xf0 && second >= 0x90) ||
                (first >= 0xf1 && first <= 0xf3) ||
                (first === 0xf4 && second <= 0x8f))) {
            text += String.fromCodePoint(((first & 0x07) << 18) |
                ((second & 0x3f) << 12) |
                ((third & 0x3f) << 6) |
                (fourth & 0x3f));
            index += 4;
            continue;
        }
        throw new TypeError("W10 control record is not valid UTF-8");
    }
    return text;
}
/** UTF-8 encoder independent of Node and DOM library declarations. */
function encodeW10Utf8(value) {
    const bytes = [];
    for (const character of value) {
        const point = character.codePointAt(0);
        if (point === undefined || (point >= 0xd800 && point <= 0xdfff))
            throw new TypeError("W10 control record contains an invalid scalar value");
        if (point <= 0x7f)
            bytes.push(point);
        else if (point <= 0x7ff) {
            bytes.push(0xc0 | (point >>> 6), 0x80 | (point & 0x3f));
        }
        else if (point <= 0xffff) {
            bytes.push(0xe0 | (point >>> 12), 0x80 | ((point >>> 6) & 0x3f), 0x80 | (point & 0x3f));
        }
        else {
            bytes.push(0xf0 | (point >>> 18), 0x80 | ((point >>> 12) & 0x3f), 0x80 | ((point >>> 6) & 0x3f), 0x80 | (point & 0x3f));
        }
    }
    return Uint8Array.from(bytes);
}
/**
 * Runtime-neutral UTF-8/JSON boundary shared by exact W10 and retained-v1
 * control records. It rejects BOMs and duplicate members at every depth but
 * deliberately does not require a canonical JSON spelling.
 */
export function parseW10ControlJsonBytes(bytes) {
    if (!(bytes instanceof Uint8Array) || bytes.byteLength === 0)
        throw new TypeError("W10 control record must be non-empty bytes");
    if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)
        throw new TypeError("W10 control record must not contain a BOM");
    let source;
    try {
        source = decodeW10Utf8(bytes);
    }
    catch (cause) {
        throw new TypeError("W10 control record is not valid UTF-8", { cause });
    }
    if (source.charCodeAt(0) === 0xfeff)
        throw new TypeError("W10 control record must not contain a BOM");
    assertW10JsonHasUniqueMembers(source);
    return JSON.parse(source);
}
/** Strict selected-v1 decoder for the exact W10-only logical record catalog. */
export function decodeW10ExactControlRecord(bytes) {
    const parsed = w10JsonRecord(parseW10ControlJsonBytes(bytes), "W10 control envelope");
    w10ExactKeys(parsed, ["v", "type", "seq", "payload"], "W10 control envelope");
    if (parsed.v !== 1)
        throw new TypeError("W10 control envelope version is invalid");
    const seq = w10PositiveSafeInteger(parsed.seq, "W10 inner sequence");
    const record = readW10ExactControlRecord(parsed.type, parsed.payload);
    return Object.freeze({
        v: 1,
        type: record.type,
        seq,
        payload: record.payload,
    });
}
/** Canonical compact UTF-8 encoder; validation also reorders no caller data. */
export function encodeW10ExactControlRecord(seq, record) {
    const parsed = readW10ExactControlRecord(record.type, record.payload);
    const envelope = {
        v: 1,
        type: parsed.type,
        seq: w10PositiveSafeInteger(seq, "W10 inner sequence"),
        payload: parsed.payload,
    };
    return encodeW10Utf8(JSON.stringify(envelope));
}
export const v1MessageSchema = (type, payloadSchema) => openObject({
    v: protocolVersionSchema,
    type: z.literal(type),
    seq: z.number().int().positive(),
    payload: payloadSchema,
});
const v1EmptyMessageSchema = (type) => openObject({
    v: protocolVersionSchema,
    type: z.literal(type),
    seq: z.number().int().positive(),
    payload: emptyPayloadSchema.optional(),
});
export const joinMessageSchema = v1MessageSchema("join", joinPayloadSchema);
export const joinAckMessageSchema = v1MessageSchema("join-ack", joinAckPayloadSchema);
export const leaveMessageSchema = v1EmptyMessageSchema("leave");
export const kickMessageSchema = v1MessageSchema("kick", kickPayloadSchema);
export const pingMessageSchema = v1EmptyMessageSchema("ping");
export const pongMessageSchema = v1EmptyMessageSchema("pong");
export const roomStateMessageSchema = v1MessageSchema("room-state", roomStatePayloadSchema);
export const syncRequestMessageSchema = v1EmptyMessageSchema("sync-request");
export const peerJoinedMessageSchema = v1MessageSchema("peer-joined", peerJoinedPayloadSchema);
export const peerLeftMessageSchema = v1MessageSchema("peer-left", peerLeftPayloadSchema);
export const peerUpdatedMessageSchema = v1MessageSchema("peer-updated", peerUpdatedPayloadSchema);
export const peerUpdateMessageSchema = v1MessageSchema("peer-update", peerUpdatePayloadSchema);
export const shareRequestMessageSchema = v1EmptyMessageSchema("share-request");
export const shareDecisionMessageSchema = v1MessageSchema("share-decision", shareDecisionPayloadSchema);
export const shareReadyMessageSchema = v1MessageSchema("share-ready", shareReadyPayloadSchema);
export const shareStopMessageSchema = v1MessageSchema("share-stop", shareStopPayloadSchema);
export const shareRevokeMessageSchema = v1MessageSchema("share-revoke", shareRevokePayloadSchema);
export const shareChangedMessageSchema = v1MessageSchema("share-changed", shareChangedPayloadSchema);
export const hostMediaMessageSchema = v1MessageSchema("host-media", hostMediaPayloadSchema);
export const legCreateMessageSchema = v1MessageSchema("leg-create", legCreatePayloadSchema);
export const legCloseMessageSchema = v1MessageSchema("leg-close", legClosePayloadSchema);
export const legStatusMessageSchema = v1MessageSchema("leg-status", legStatusPayloadSchema);
export const transportChangedMessageSchema = v1MessageSchema("transport-changed", transportChangedPayloadSchema);
export const clientRtcSignalMessageSchema = v1MessageSchema("rtc-signal", clientRtcSignalPayloadSchema);
export const rtcSignalMessageSchema = v1MessageSchema("rtc-signal", rtcSignalPayloadSchema);
export const anyRtcSignalMessageSchema = v1MessageSchema("rtc-signal", anyRtcSignalPayloadSchema);
export const statsReportMessageSchema = v1MessageSchema("stats-report", statsReportPayloadSchema);
export const errorMessageSchema = v1MessageSchema("error", errorPayloadSchema);
export const debugForceTransportMessageSchema = v1MessageSchema("debug-force-transport", debugForceTransportPayloadSchema);
const MAGIC = Uint8Array.of(0x4b, 0x53, 0x44, 0x43);
const START_FLAG = 0x02;
const END_FLAG = 0x01;
const UINT32_MAX = 0xffff_ffff;
function fail(message) {
    throw new TypeError(message);
}
function requireLane(value) {
    if (value !== "control" && value !== "whiteboard")
        fail("W10 data-channel lane must be control or whiteboard");
}
function requireUint8(value, label) {
    if (!Number.isInteger(value) || value < 0 || value > 0xff)
        fail(`${label} must be uint8`);
}
function requirePositiveUint32(value, label) {
    if (!Number.isInteger(value) || value <= 0 || value > UINT32_MAX)
        fail(`${label} must be a positive uint32`);
}
function expectedFlags(chunkIndex, chunkCount) {
    return ((chunkIndex === 0 ? START_FLAG : 0) |
        (chunkIndex === chunkCount - 1 ? END_FLAG : 0));
}
function maximumMessageByteLength(lane) {
    return lane === "control"
        ? W10_DATA_CHANNEL_V1.maxControlMessageByteLength
        : W10_DATA_CHANNEL_V1.maxWhiteboardMessageByteLength;
}
function validateKind(lane, wireVersion, kind) {
    requireUint8(wireVersion, "W10 data-channel wire version");
    requireUint8(kind, "W10 data-channel kind");
    if (wireVersion === 0) {
        if (lane !== "control" ||
            (kind !== W10_DATA_CHANNEL_KINDS.hello &&
                kind !== W10_DATA_CHANNEL_KINDS.ready &&
                kind !== W10_DATA_CHANNEL_KINDS.reject)) {
            fail("W10 negotiation frame kind/lane is invalid");
        }
        return;
    }
    if (wireVersion !== W10_DATA_CHANNEL_V1.wireVersion)
        fail(`W10 data-channel has no registered decoder for version ${wireVersion}`);
    const expected = lane === "control"
        ? W10_DATA_CHANNEL_KINDS.control
        : W10_DATA_CHANNEL_KINDS.whiteboard;
    if (kind !== expected)
        fail("W10 application frame kind does not match its lane");
}
export function chunkW10DataChannelMessage(input) {
    requireLane(input.lane);
    validateKind(input.lane, input.wireVersion, input.kind);
    requirePositiveUint32(input.messageId, "W10 data-channel message ID");
    if (!(input.payload instanceof Uint8Array) || input.payload.byteLength === 0)
        fail("W10 data-channel payload must be non-empty Uint8Array");
    if (input.payload.byteLength > maximumMessageByteLength(input.lane))
        fail(`W10 ${input.lane} logical message exceeds its byte ceiling`);
    const chunkCount = Math.ceil(input.payload.byteLength / W10_DATA_CHANNEL_V1.maxChunkPayloadByteLength);
    if (chunkCount > 0xffff)
        fail("W10 data-channel chunk count exceeds uint16");
    const frames = [];
    for (let chunkIndex = 0; chunkIndex < chunkCount; chunkIndex += 1) {
        const start = chunkIndex * W10_DATA_CHANNEL_V1.maxChunkPayloadByteLength;
        const end = Math.min(input.payload.byteLength, start + W10_DATA_CHANNEL_V1.maxChunkPayloadByteLength);
        const chunk = input.payload.subarray(start, end);
        const frame = new Uint8Array(W10_DATA_CHANNEL_V1.headerByteLength + chunk.byteLength);
        frame.set(MAGIC, 0);
        frame[4] = input.wireVersion;
        frame[5] = input.kind;
        frame[6] = expectedFlags(chunkIndex, chunkCount);
        frame[7] = W10_DATA_CHANNEL_V1.headerByteLength;
        const view = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
        view.setUint32(8, input.messageId, false);
        view.setUint16(12, chunkIndex, false);
        view.setUint16(14, chunkCount, false);
        view.setUint32(16, input.payload.byteLength, false);
        frame.set(chunk, W10_DATA_CHANNEL_V1.headerByteLength);
        frames.push(frame);
    }
    return frames;
}
export function decodeW10DataChannelFrame(input, expectedLane) {
    requireLane(expectedLane);
    if (!(input instanceof Uint8Array))
        fail("W10 data-channel frame must be Uint8Array");
    if (input.byteLength <= W10_DATA_CHANNEL_V1.headerByteLength)
        fail("W10 data-channel frame is truncated or has empty payload");
    if (input.byteLength > W10_DATA_CHANNEL_V1.maxSendByteLength)
        fail("W10 data-channel frame exceeds 16 KiB");
    for (let index = 0; index < MAGIC.byteLength; index += 1) {
        if (input[index] !== MAGIC[index])
            fail("W10 data-channel magic is invalid");
    }
    const wireVersion = input[4];
    const kind = input[5];
    const flags = input[6];
    if ((flags & ~(START_FLAG | END_FLAG)) !== 0)
        fail("W10 data-channel frame has reserved flag bits");
    if (input[7] !== W10_DATA_CHANNEL_V1.headerByteLength)
        fail("W10 data-channel header length is invalid");
    validateKind(expectedLane, wireVersion, kind);
    const view = new DataView(input.buffer, input.byteOffset, input.byteLength);
    const messageId = view.getUint32(8, false);
    const chunkIndex = view.getUint16(12, false);
    const chunkCount = view.getUint16(14, false);
    const totalPayloadByteLength = view.getUint32(16, false);
    requirePositiveUint32(messageId, "W10 data-channel message ID");
    if (chunkCount === 0 || chunkIndex >= chunkCount)
        fail("W10 data-channel chunk index/count is invalid");
    if (totalPayloadByteLength === 0)
        fail("W10 data-channel logical payload must be non-empty");
    if (totalPayloadByteLength > maximumMessageByteLength(expectedLane))
        fail(`W10 ${expectedLane} inbound logical message exceeds its byte ceiling`);
    const canonicalChunkCount = Math.ceil(totalPayloadByteLength / W10_DATA_CHANNEL_V1.maxChunkPayloadByteLength);
    if (chunkCount !== canonicalChunkCount)
        fail("W10 data-channel chunk count is not canonical");
    if (flags !== expectedFlags(chunkIndex, chunkCount))
        fail("W10 data-channel START/END flags are not canonical");
    const expectedPayloadByteLength = chunkIndex < chunkCount - 1
        ? W10_DATA_CHANNEL_V1.maxChunkPayloadByteLength
        : totalPayloadByteLength -
            (chunkCount - 1) * W10_DATA_CHANNEL_V1.maxChunkPayloadByteLength;
    if (input.byteLength - W10_DATA_CHANNEL_V1.headerByteLength !==
        expectedPayloadByteLength) {
        fail("W10 data-channel chunk payload length is not canonical");
    }
    return Object.freeze({
        lane: expectedLane,
        wireVersion,
        kind,
        flags,
        messageId,
        chunkIndex,
        chunkCount,
        totalPayloadByteLength,
        payload: input.slice(W10_DATA_CHANNEL_V1.headerByteLength),
    });
}
export function reassembleW10DataChannelMessage(frames, expectedLane, expectedWireVersion) {
    requireLane(expectedLane);
    if (!Array.isArray(frames) || frames.length === 0)
        fail("W10 data-channel message is missing every chunk");
    const decoded = frames.map((frame) => decodeW10DataChannelFrame(frame, expectedLane));
    const first = decoded[0];
    if (first === undefined)
        fail("W10 data-channel message is missing every chunk");
    if (expectedWireVersion !== undefined &&
        first.wireVersion !== expectedWireVersion) {
        fail("W10 data-channel frame version differs from negotiation");
    }
    if (first.chunkCount !== decoded.length)
        fail("W10 data-channel message has missing or extra chunks");
    const canonicalCount = Math.ceil(first.totalPayloadByteLength /
        W10_DATA_CHANNEL_V1.maxChunkPayloadByteLength);
    if (first.chunkCount !== canonicalCount)
        fail("W10 data-channel chunk count is not canonical");
    let total = 0;
    decoded.forEach((chunk, index) => {
        if (chunk.wireVersion !== first.wireVersion ||
            chunk.kind !== first.kind ||
            chunk.messageId !== first.messageId ||
            chunk.chunkCount !== first.chunkCount ||
            chunk.totalPayloadByteLength !== first.totalPayloadByteLength) {
            fail("W10 data-channel chunks disagree on message identity");
        }
        if (chunk.chunkIndex !== index)
            fail("W10 data-channel chunks are duplicate or out of order");
        if (chunk.flags !== expectedFlags(index, decoded.length))
            fail("W10 data-channel START/END flags are not canonical");
        if (index < decoded.length - 1 &&
            chunk.payload.byteLength !== W10_DATA_CHANNEL_V1.maxChunkPayloadByteLength) {
            fail("W10 data-channel non-final chunk is not full");
        }
        total += chunk.payload.byteLength;
    });
    if (total !== first.totalPayloadByteLength)
        fail("W10 data-channel payload length does not match its header");
    const payload = new Uint8Array(total);
    let offset = 0;
    for (const chunk of decoded) {
        payload.set(chunk.payload, offset);
        offset += chunk.payload.byteLength;
    }
    return Object.freeze({
        lane: expectedLane,
        wireVersion: first.wireVersion,
        kind: first.kind,
        messageId: first.messageId,
        payload,
    });
}
export function createW10DataChannelLaneReceiver(options) {
    requireLane(options.lane);
    const initialMessageId = options.initialMessageId ?? 1;
    requirePositiveUint32(initialMessageId, "W10 initial message ID");
    let expectedMessageId = initialMessageId;
    return {
        receive(frames, expectedWireVersion) {
            if (expectedMessageId === undefined)
                fail("W10 data-channel message ID sequence is exhausted");
            const result = reassembleW10DataChannelMessage(frames, options.lane, expectedWireVersion);
            if (result.messageId !== expectedMessageId)
                fail("W10 data-channel logical message is replayed or skips its expected ID");
            expectedMessageId =
                expectedMessageId === UINT32_MAX ? undefined : expectedMessageId + 1;
            return result;
        },
        get expectedMessageId() {
            return expectedMessageId;
        },
    };
}
function requireVersion(value, label) {
    if (!Number.isInteger(value) || value <= 0 || value > 0xff)
        fail(`${label} must fit the uint8 wire-version field`);
}
export function negotiateW10DataChannelVersion(options) {
    requireVersion(options.localMinimum, "W10 localMinimum");
    requireVersion(options.localMaximum, "W10 localMaximum");
    requireVersion(options.remoteMinimum, "W10 remoteMinimum");
    requireVersion(options.remoteMaximum, "W10 remoteMaximum");
    if (options.localMinimum > options.localMaximum ||
        options.remoteMinimum > options.remoteMaximum) {
        fail("W10 version range minimum exceeds maximum");
    }
    const minimum = Math.max(options.localMinimum, options.remoteMinimum);
    const maximum = Math.min(options.localMaximum, options.remoteMaximum);
    return minimum <= maximum ? maximum : undefined;
}
function encodeUint16(value) {
    const bytes = new Uint8Array(2);
    new DataView(bytes.buffer).setUint16(0, value, false);
    return bytes;
}
function requireBytes(value, length, label) {
    if (!(value instanceof Uint8Array) || value.byteLength !== length)
        fail(`${label} must be exactly ${length} bytes`);
}
export function encodeW10VersionRangePayload(minimum, maximum) {
    negotiateW10DataChannelVersion({
        localMinimum: minimum,
        localMaximum: maximum,
        remoteMinimum: minimum,
        remoteMaximum: maximum,
    });
    const bytes = new Uint8Array(4);
    const view = new DataView(bytes.buffer);
    view.setUint16(0, minimum, false);
    view.setUint16(2, maximum, false);
    return bytes;
}
export function decodeW10VersionRangePayload(payload) {
    requireBytes(payload, 4, "W10 HELLO version range");
    const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
    const minimum = view.getUint16(0, false);
    const maximum = view.getUint16(2, false);
    negotiateW10DataChannelVersion({
        localMinimum: minimum,
        localMaximum: maximum,
        remoteMinimum: minimum,
        remoteMaximum: maximum,
    });
    return Object.freeze({ minimum, maximum });
}
export function respondToW10VersionHello(options) {
    const remote = decodeW10VersionRangePayload(options.helloPayload);
    const selectedVersion = negotiateW10DataChannelVersion({
        localMinimum: options.localMinimum,
        localMaximum: options.localMaximum,
        remoteMinimum: remote.minimum,
        remoteMaximum: remote.maximum,
    });
    return selectedVersion === undefined
        ? Object.freeze({
            type: "reject",
            reasonCode: 1,
            payload: encodeUint16(1),
        })
        : Object.freeze({
            type: "ready",
            selectedVersion,
            payload: encodeUint16(selectedVersion),
        });
}
export function decodeW10ReadyPayload(payload) {
    requireBytes(payload, 2, "W10 READY selected version");
    const selectedVersion = new DataView(payload.buffer, payload.byteOffset, payload.byteLength).getUint16(0, false);
    requireVersion(selectedVersion, "W10 READY selected version");
    return selectedVersion;
}
export function decodeW10RejectPayload(payload) {
    requireBytes(payload, 2, "W10 REJECT reason");
    const reason = new DataView(payload.buffer, payload.byteOffset, payload.byteLength).getUint16(0, false);
    if (reason !== 1)
        fail("W10 REJECT reason is not NO_COMMON_VERSION");
    return 1;
}
//# sourceMappingURL=schemas.js.map